import React, { useCallback, useEffect, useMemo, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  ArrowPathIcon,
  ArrowUturnLeftIcon,
  CheckIcon,
  EllipsisVerticalIcon,
  EnvelopeIcon,
  FolderArrowDownIcon,
  MagnifyingGlassIcon,
  MinusIcon,
  PaperAirplaneIcon,
  PaperClipIcon,
  PencilSquareIcon,
  StarIcon,
  TrashIcon,
  UsersIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { fetchDataAlleGebruikersPage } from "../../apiCalls/usersApi";
import { sanitizeHtml } from "../../utils/sanitizeHtml";
import {
  deleteAdminMailboxMessage,
  downloadAdminMailboxAttachment,
  fetchAdminMailboxFolders,
  fetchAdminMailboxMessage,
  fetchAdminMailboxMessages,
  markAdminMailboxMessageSeen,
  moveAdminMailboxMessage,
  saveAdminMailboxDraft,
  sendAdminMailboxMessage,
  toggleAdminMailboxMessageFlag,
} from "./adminMailboxApi";
import {
  buildAdminMailboxFolders,
  buildAdminMailboxMoveFolders,
} from "./adminMailboxFolders";
import { wa } from "../Mail/mailWaTheme";
import "./AdminMailbox.css";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

function formatDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const today = new Date();
  if (date.toDateString() === today.toDateString()) {
    return date.toLocaleTimeString("nl-BE", { hour: "2-digit", minute: "2-digit" });
  }
  return date.toLocaleDateString("nl-BE", { day: "numeric", month: "numeric", year: "numeric" });
}

function splitRecipients(value) {
  if (Array.isArray(value)) return value.map((item) => String(item).trim()).filter(Boolean);
  return String(value || "").split(/[;,]/).map((item) => item.trim()).filter(Boolean);
}

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
}

function normalizeRecipient(user) {
  const email = String(user?.email || user?.userdetails_email || user?.username || "").trim();
  if (!isValidEmail(email)) return null;
  const name = String(
    user?.full_name ||
      user?.userdetails_full_name ||
      [user?.first_name || user?.userdetails_first_name, user?.last_name || user?.userdetails_last_name]
        .filter(Boolean)
        .join(" ") ||
      email
  ).trim();
  const role = Number(user?.role ?? user?.role_id ?? user?.userdetails_role_id);
  return {
    id: String(user?.id || user?.user_id || user?.userdetails_user_id || email),
    name,
    email,
    role,
  };
}

function roleLabel(role) {
  if (Number(role) === 1) return "Therapeut";
  if (Number(role) === 2) return "Patient";
  if (Number(role) === 3) return "Stagiaire";
  return "Gebruiker";
}

function uniqueRecipients(recipients) {
  const seen = new Set();
  return recipients.filter((recipient) => {
    const key = String(recipient || "").toLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function textFromHtml(value) {
  const html = sanitizeHtml(value);
  if (!html) return "";
  if (typeof document !== "undefined") {
    const element = document.createElement("div");
    element.innerHTML = html;
    return element.textContent || element.innerText || "";
  }
  return html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
}

function escapeHtml(value) {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function nlToHtml(value) {
  return escapeHtml(value).replace(/\n/g, "<br/>");
}

function buildQuotedBody(message, label) {
  return [
    "<p><br/></p>",
    "<hr/>",
    `<p><strong>${label}</strong></p>`,
    `<p><strong>Van:</strong> ${escapeHtml(message.sender || "")}<br/>`,
    `<strong>Datum:</strong> ${escapeHtml(formatDate(message.date))}<br/>`,
    `<strong>Onderwerp:</strong> ${escapeHtml(message.subject || "")}</p>`,
    `<p>${nlToHtml(message.text || "")}</p>`,
  ].join("");
}

const richTextModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ color: [] }, { background: [] }],
    [{ align: [] }],
    ["link"],
    ["clean"],
  ],
};

const richTextFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "list",
  "bullet",
  "color",
  "background",
  "align",
  "link",
];

function RecipientPicker({
  field,
  label,
  value,
  onChange,
  recipients,
  loading,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const selected = useMemo(() => new Set(value.map((item) => item.toLowerCase())), [value]);
  const filteredRecipients = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const source = needle
      ? recipients.filter((recipient) =>
          [recipient.name, recipient.email, roleLabel(recipient.role)]
            .join(" ")
            .toLowerCase()
            .includes(needle)
        )
      : recipients;
    return source.slice(0, 40);
  }, [recipients, search]);
  const groups = useMemo(
    () => [
      { label: "Alle gebruikers", recipients },
      { label: "Alle patienten", recipients: recipients.filter((recipient) => recipient.role === 2) },
      { label: "Alle therapeuten", recipients: recipients.filter((recipient) => recipient.role === 1) },
      { label: "Alle stagiaires", recipients: recipients.filter((recipient) => recipient.role === 3) },
    ],
    [recipients]
  );

  const addRecipients = (items) => {
    onChange(uniqueRecipients([...value, ...items.map((item) => item.email)]));
  };
  const toggleRecipient = (recipient) => {
    if (selected.has(recipient.email.toLowerCase())) {
      onChange(value.filter((email) => email.toLowerCase() !== recipient.email.toLowerCase()));
      return;
    }
    onChange(uniqueRecipients([...value, recipient.email]));
  };

  return (
    <div className="relative border-b border-wa-border px-5 py-3">
      <div className="flex items-start gap-3">
        <span className={classNames("w-20 flex-shrink-0 pt-2 text-sm", wa.muted)}>{label}</span>
        <div className="min-w-0 flex-1">
          <div className="flex min-h-10 flex-wrap items-center gap-2 rounded-md border border-transparent bg-wa-panel2 px-2 py-1.5 focus-within:border-[#0b84d8] focus-within:ring-2 focus-within:ring-[#0b84d8]/30">
            {value.map((email) => (
              <button
                key={email}
                type="button"
                onClick={() => onChange(value.filter((item) => item !== email))}
                className="inline-flex max-w-full items-center gap-1 rounded-full bg-[#0b84d8]/12 px-2.5 py-1 text-xs font-medium text-[#0b84d8] hover:bg-[#0b84d8]/20"
                title={`${email} verwijderen`}
              >
                <span className="truncate">{email}</span>
                <XMarkIcon className="h-3.5 w-3.5" />
              </button>
            ))}
            <input
              value={search}
              onFocus={() => setOpen(true)}
              onChange={(event) => {
                setSearch(event.target.value);
                setOpen(true);
              }}
              onKeyDown={(event) => {
                if (event.key !== "Enter") return;
                event.preventDefault();
                const email = search.trim();
                if (!isValidEmail(email)) return;
                onChange(uniqueRecipients([...value, email]));
                setSearch("");
                setOpen(false);
              }}
              placeholder={value.length ? "Nog iemand toevoegen..." : "Zoek naam of e-mailadres..."}
              className="h-7 min-w-[12rem] flex-1 bg-transparent text-sm outline-none placeholder:text-wa-muted"
              aria-label={`${label} ontvangers zoeken`}
            />
          </div>
        </div>
      </div>
      {open ? (
        <div className="absolute left-[7.75rem] right-5 top-[3.7rem] z-20 overflow-hidden rounded-lg border border-wa-border bg-wa-panel shadow-2xl">
          <div className="grid gap-1 border-b border-wa-border p-2 sm:grid-cols-2">
            {groups.map((group) => (
              <button
                key={`${field}-${group.label}`}
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => addRecipients(group.recipients)}
                disabled={!group.recipients.length}
                className="flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs font-semibold text-wa-text hover:bg-wa-hover disabled:cursor-not-allowed disabled:opacity-45"
              >
                <UsersIcon className="h-4 w-4 text-wa-muted" />
                <span>{group.label}</span>
                <span className="ml-auto text-wa-muted">{group.recipients.length}</span>
              </button>
            ))}
          </div>
          <div className="max-h-56 overflow-y-auto p-1">
            {loading ? (
              <div className="px-3 py-4 text-sm text-wa-muted">Gebruikers laden...</div>
            ) : filteredRecipients.length ? (
              filteredRecipients.map((recipient) => {
                const checked = selected.has(recipient.email.toLowerCase());
                return (
                  <button
                    key={`${field}-${recipient.id}-${recipient.email}`}
                    type="button"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => toggleRecipient(recipient)}
                    className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left hover:bg-wa-hover"
                  >
                    <span className={classNames("flex h-4 w-4 items-center justify-center rounded border", checked ? "border-[#0b84d8] bg-[#0b84d8] text-white" : "border-wa-border")}>
                      {checked ? <CheckIcon className="h-3 w-3" /> : null}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-wa-text">{recipient.name}</span>
                      <span className="block truncate text-xs text-wa-muted">{recipient.email} · {roleLabel(recipient.role)}</span>
                    </span>
                  </button>
                );
              })
            ) : (
              <div className="px-3 py-4 text-sm text-wa-muted">Geen gebruiker met e-mailadres gevonden.</div>
            )}
          </div>
          <div className="border-t border-wa-border p-2 text-right">
            <button type="button" onClick={() => setOpen(false)} className="rounded-md px-3 py-1.5 text-xs font-semibold text-wa-muted hover:bg-wa-hover hover:text-wa-text">
              Klaar
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

const AdminMailbox = () => {
  const [activeFolder, setActiveFolder] = useState("INBOX");
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [selectedId, setSelectedId] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [messageDetailOpen, setMessageDetailOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [loadingList, setLoadingList] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState(false);
  const [error, setError] = useState("");
  const [setup, setSetup] = useState(null);
  const [imapFolders, setImapFolders] = useState([]);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeMinimized, setComposeMinimized] = useState(false);
  const [sending, setSending] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [sendStatus, setSendStatus] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [mailConfig, setMailConfig] = useState(null);
  const [recipientOptions, setRecipientOptions] = useState([]);
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [togglingFlagIds, setTogglingFlagIds] = useState([]);
  const [compose, setCompose] = useState({
    to: [],
    cc: [],
    bcc: [],
    subject: "",
    body: "",
  });

  const folderNavigation = useMemo(
    () => buildAdminMailboxFolders(imapFolders),
    [imapFolders]
  );
  const moveFolders = useMemo(
    () => buildAdminMailboxMoveFolders(imapFolders),
    [imapFolders]
  );
  const targetFolders = useMemo(
    () => moveFolders.filter((item) => item.path && item.path !== activeFolder),
    [activeFolder, moveFolders]
  );
  const sentFolderPath =
    folderNavigation.find((folder) => folder.name === "Verzonden")?.path || "Sent";
  const actionableIds = selectedIds.length ? selectedIds : selectedId ? [selectedId] : [];
  const canDeleteActiveMessage = actionableIds.length > 0 && activeFolder === sentFolderPath;

  const loadFolders = useCallback(async () => {
    try {
      const result = await fetchAdminMailboxFolders();
      if (result.configured === false) {
        setSetup(result);
        setImapFolders([]);
        return;
      }
      setSetup(null);
      setMailConfig(result.config || null);
      setImapFolders(result.folders || []);
    } catch (err) {
      setMailConfig(err?.response?.data?.config || null);
      setError(err?.response?.data?.error || err?.response?.data?.message || "E-mailmappen konden niet geladen worden.");
    }
  }, []);

  const loadMessages = useCallback(async () => {
    setLoadingList(true);
    setError("");
    try {
      const result = await fetchAdminMailboxMessages({ folder: activeFolder, limit: 50 });
      if (result.configured === false) {
        setSetup(result);
        setMessages([]);
        setSelectedMessage(null);
        setSelectedId("");
        setSelectedIds([]);
        return;
      }
      setSetup(null);
      setMailConfig(result.config || null);
      const nextMessages = result.messages || [];
      setMessages(nextMessages);
      setSelectedId((current) =>
        current && nextMessages.some((message) => message.id === current)
          ? current
          : ""
      );
      setSelectedIds((current) =>
        current.filter((id) => nextMessages.some((message) => message.id === id))
      );
    } catch (err) {
      setMailConfig(err?.response?.data?.config || null);
      const serverError = err?.response?.data?.error || err?.response?.data?.message;
      setError(serverError ? `De inkomende mailserver weigert de aanvraag: ${serverError}` : "E-mails konden niet geladen worden.");
    } finally {
      setLoadingList(false);
    }
  }, [activeFolder]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  useEffect(() => {
    loadFolders();
  }, [loadFolders]);

  useEffect(() => {
    if (!composeOpen || recipientOptions.length) return;

    let active = true;
    setLoadingRecipients(true);
    fetchDataAlleGebruikersPage(0, 500, undefined, 999, "all", "")
      .then((rows) => {
        if (!active) return;
        const recipients = rows
          .map(normalizeRecipient)
          .filter(Boolean)
          .filter((recipient, index, list) =>
            list.findIndex((item) => item.email.toLowerCase() === recipient.email.toLowerCase()) === index
          )
          .sort((a, b) => a.name.localeCompare(b.name, "nl-BE"));
        setRecipientOptions(recipients);
      })
      .catch(() => {
        if (active) setRecipientOptions([]);
      })
      .finally(() => {
        if (active) setLoadingRecipients(false);
      });

    return () => {
      active = false;
    };
  }, [composeOpen, recipientOptions.length]);

  useEffect(() => {
    if (!selectedId || setup) {
      setSelectedMessage(null);
      return;
    }

    let active = true;
    setLoadingMessage(true);
    fetchAdminMailboxMessage(selectedId, { folder: activeFolder })
      .then((result) => {
        if (!active) return;
        setSelectedMessage(result.message || null);
      })
      .catch((err) => {
        if (!active) return;
        setMailConfig(err?.response?.data?.config || null);
        setError(err?.response?.data?.error || err?.response?.data?.message || "E-mail kon niet geopend worden.");
      })
      .finally(() => {
        if (active) setLoadingMessage(false);
      });

    return () => {
      active = false;
    };
  }, [activeFolder, selectedId, setup]);

  const filteredMessages = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return messages;
    return messages.filter((message) =>
      [message.sender, message.senderEmail, message.subject, message.snippet]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(needle))
    );
  }, [messages, query]);

  const selectedHtml = useMemo(() => sanitizeHtml(selectedMessage?.html), [selectedMessage?.html]);

  const openCompose = (mode = "new", message = selectedMessage) => {
    if (mode === "reply" && message) {
      setCompose({
        to: message.senderEmail ? [message.senderEmail] : splitRecipients(message.sender),
        cc: [],
        bcc: [],
        subject: message.subject?.startsWith("Re:") ? message.subject : `Re: ${message.subject || ""}`,
        body: buildQuotedBody(message, "Origineel bericht"),
      });
    } else if (mode === "replyAll" && message) {
      setCompose({
        to: uniqueRecipients([
          ...(message.senderEmail ? [message.senderEmail] : splitRecipients(message.sender)),
          ...(message.to || []),
        ]),
        cc: [],
        bcc: [],
        subject: message.subject?.startsWith("Re:") ? message.subject : `Re: ${message.subject || ""}`,
        body: buildQuotedBody(message, "Origineel bericht"),
      });
    } else if (mode === "forward" && message) {
      setCompose({
        to: [],
        cc: [],
        bcc: [],
        subject: message.subject?.startsWith("Fw:") ? message.subject : `Fw: ${message.subject || ""}`,
        body: buildQuotedBody(message, "Doorgestuurd bericht"),
      });
    } else {
      setCompose({ to: [], cc: [], bcc: [], subject: "", body: "" });
      setAttachments([]);
    }
    setSendStatus("");
    setComposeMinimized(false);
    setComposeOpen(true);
  };

  const handleOpenMessage = (id) => {
    setSelectedId(id);
    setMessageDetailOpen(true);
  };

  const updateMessageFlagState = (id, flagged) => {
    setMessages((current) =>
      current.map((message) =>
        message.id === id ? { ...message, flagged } : message
      )
    );
    setSelectedMessage((current) =>
      current?.id === id ? { ...current, flagged } : current
    );
  };

  const handleSelectMessageCheckbox = (message, event) => {
    event.stopPropagation();
    setSelectedIds((current) => {
      if (current.includes(message.id)) {
        return current.filter((id) => id !== message.id);
      }
      return [...current, message.id];
    });
    setSelectedId(message.id);
  };

  const handleToggleFavorite = async (message, event) => {
    event.stopPropagation();
    const nextFlagged = !message.flagged;
    setTogglingFlagIds((current) => [...current, message.id]);
    updateMessageFlagState(message.id, nextFlagged);

    try {
      await toggleAdminMailboxMessageFlag(message.id, {
        folder: activeFolder,
        flagged: nextFlagged,
      });
    } catch (err) {
      updateMessageFlagState(message.id, message.flagged);
      setError(err?.response?.data?.error || err?.response?.data?.message || "Favoriet kon niet aangepast worden.");
    } finally {
      setTogglingFlagIds((current) => current.filter((id) => id !== message.id));
    }
  };

  const handleMoveMessage = async (targetFolder) => {
    const ids = actionableIds;
    if (ids.length === 0 || !targetFolder) return;
    setError("");
    try {
      await Promise.all(
        ids.map((id) =>
          moveAdminMailboxMessage(id, {
            folder: activeFolder,
            targetFolder,
          })
        )
      );
      setMessageDetailOpen(false);
      setSelectedMessage(null);
      setSelectedId("");
      setSelectedIds([]);
      loadMessages();
    } catch (err) {
      setError(err?.response?.data?.error || err?.response?.data?.message || "E-mail kon niet verplaatst worden.");
    }
  };

  const handleDeleteMessage = async () => {
    if (!canDeleteActiveMessage) return;
    setError("");
    try {
      await Promise.all(
        actionableIds.map((id) => deleteAdminMailboxMessage(id, { folder: activeFolder }))
      );
      setMessageDetailOpen(false);
      setSelectedMessage(null);
      setSelectedId("");
      setSelectedIds([]);
      loadMessages();
    } catch (err) {
      setError(err?.response?.data?.error || err?.response?.data?.message || "E-mail kon niet verwijderd worden.");
    }
  };

  const updateMessageSeenState = (id, seen) => {
    setMessages((current) =>
      current.map((message) =>
        message.id === id ? { ...message, seen } : message
      )
    );
    setSelectedMessage((current) =>
      current?.id === id ? { ...current, seen } : current
    );
  };

  const handleMarkSeen = async (seen) => {
    const ids = actionableIds;
    if (ids.length === 0) return;
    setError("");
    ids.forEach((id) => updateMessageSeenState(id, seen));

    try {
      await Promise.all(
        ids.map((id) =>
          markAdminMailboxMessageSeen(id, {
            folder: activeFolder,
            seen,
          })
        )
      );
    } catch (err) {
      loadMessages();
      setError(err?.response?.data?.error || err?.response?.data?.message || "Leesstatus kon niet aangepast worden.");
    }
  };

  const handleDownloadAttachment = async (attachment) => {
    if (!selectedId || !attachment?.id) return;
    const success = await downloadAdminMailboxAttachment(selectedId, attachment.id, {
      folder: activeFolder,
      filename: attachment.filename,
    });
    if (!success) setError("Bijlage kon niet gedownload worden.");
  };

  const handleSend = async () => {
    setSending(true);
    setSendStatus("");
    try {
      const html = sanitizeHtml(compose.body);
      const text = textFromHtml(html);
      const result = await sendAdminMailboxMessage({
        to: splitRecipients(compose.to),
        cc: splitRecipients(compose.cc),
        bcc: splitRecipients(compose.bcc),
        subject: compose.subject,
        text,
        html,
        attachments,
      });
      if (result.success) {
        setSendStatus("Bericht verzonden.");
        setCompose({ to: [], cc: [], bcc: [], subject: "", body: "" });
        setAttachments([]);
        setComposeMinimized(false);
        setComposeOpen(false);
      } else {
        setSendStatus(result.message || "Bericht kon niet verzonden worden.");
      }
    } catch (err) {
      setSendStatus(err?.response?.data?.error || err?.response?.data?.message || "Bericht kon niet verzonden worden.");
    } finally {
      setSending(false);
    }
  };

  const handleSaveDraft = async () => {
    setSavingDraft(true);
    setSendStatus("");
    try {
      const html = sanitizeHtml(compose.body);
      const text = textFromHtml(html);
      const draftFolder =
        folderNavigation.find((folder) => folder.name === "Concepten")?.path || "Drafts";
      const result = await saveAdminMailboxDraft({
        folder: draftFolder,
        to: splitRecipients(compose.to),
        cc: splitRecipients(compose.cc),
        bcc: splitRecipients(compose.bcc),
        subject: compose.subject,
        text,
        html,
      });
      setSendStatus(result.success ? "Concept opgeslagen." : result.message || "Concept kon niet opgeslagen worden.");
      if (activeFolder === draftFolder) loadMessages();
    } catch (err) {
      setSendStatus(err?.response?.data?.error || err?.response?.data?.message || "Concept kon niet opgeslagen worden.");
    } finally {
      setSavingDraft(false);
    }
  };

  const handleAttachmentChange = async (event) => {
    const files = Array.from(event.target.files || []);
    const encoded = await Promise.all(
      files.map(
        (file) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              const value = String(reader.result || "");
              resolve({
                id: `${file.name}-${file.size}-${file.lastModified}`,
                filename: file.name,
                contentType: file.type,
                size: file.size,
                content: value.includes(",") ? value.split(",").pop() : value,
              });
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
          })
      )
    );
    setAttachments((current) => [...current, ...encoded]);
    event.target.value = "";
  };

  return (
    <div className={classNames("flex h-full min-h-0 w-full overflow-hidden", wa.app, wa.text)}>
      <aside className={classNames("hidden w-72 flex-shrink-0 flex-col border-r md:flex", wa.border, wa.panel)}>
        <div className={classNames("border-b p-4", wa.border)}>
          <button
            type="button"
            onClick={() => openCompose("new")}
            className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-md bg-[#0b84d8] px-4 text-sm font-semibold text-white transition hover:bg-[#1494ee]"
          >
            <PencilSquareIcon className="h-5 w-5" />
            Nieuw bericht
          </button>
        </div>
        <nav className="min-h-0 flex-1 overflow-y-auto p-2" aria-label="Admin mailbox folders">
          {folderNavigation.map((item) => {
            const active = item.path === activeFolder;
            return (
              <button
                key={item.path}
                type="button"
                onClick={() => {
                  setActiveFolder(item.path);
                  setSelectedId("");
                  setSelectedIds([]);
                  setSelectedMessage(null);
                  setMessageDetailOpen(false);
                }}
                className={classNames(
                  "flex w-full items-center gap-3 rounded-md px-3 py-3 text-left text-sm font-medium transition",
                  active ? "bg-[#1f4158] text-white" : classNames(wa.muted, "hover:bg-wa-hover hover:text-wa-text")
                )}
              >
                <item.icon className="h-5 w-5 flex-shrink-0" />
                <span className="min-w-0 flex-1 truncate">{item.name}</span>
              </button>
            );
          })}
        </nav>
        <div className={classNames("border-t px-4 py-3 text-xs", wa.border, wa.muted)}>
          {mailConfig?.smtp?.user || mailConfig?.from || "info@repeathealth.com"} · SMTP{" "}
          {mailConfig?.smtp?.host || "send.one.com"}:{mailConfig?.smtp?.port || 587} · IMAP{" "}
          {mailConfig?.imap?.host || "imap.one.com"}:{mailConfig?.imap?.port || 993}
        </div>
      </aside>

      <section className={classNames("flex min-h-0 min-w-0 flex-1 flex-col", wa.border)}>
        <div className={classNames("flex h-14 flex-shrink-0 items-center gap-2 border-b px-4", wa.border, wa.panel)}>
          <button type="button" onClick={handleDeleteMessage} disabled={!canDeleteActiveMessage} className={classNames("inline-flex h-9 items-center gap-2 rounded-md px-3 text-sm font-medium transition", canDeleteActiveMessage ? "text-wa-text hover:bg-wa-hover" : "cursor-not-allowed text-wa-muted opacity-45")}>
            <TrashIcon className="h-4 w-4" />
            Verwijderen
          </button>
          <button type="button" onClick={() => openCompose("reply")} disabled={!selectedMessage || selectedIds.length > 1} className={classNames("inline-flex h-9 items-center gap-2 rounded-md px-3 text-sm font-medium transition", selectedMessage && selectedIds.length <= 1 ? "text-wa-text hover:bg-wa-hover" : "cursor-not-allowed text-wa-muted opacity-45")}>
            <ArrowUturnLeftIcon className="h-4 w-4" />
            Beantwoorden
          </button>
        </div>
        <header className={classNames("flex h-16 flex-shrink-0 items-center gap-3 border-b px-4", wa.border, wa.panel2)}>
          <button
            type="button"
            onClick={() => openCompose("new")}
            className="inline-flex h-10 items-center gap-2 rounded-md bg-[#0b84d8] px-3 text-sm font-semibold text-white md:hidden"
          >
            <PencilSquareIcon className="h-5 w-5" />
            Nieuw
          </button>
          <div className="relative min-w-0 flex-1">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-2.5 h-5 w-5 text-wa-muted" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="E-mail zoeken"
              className={classNames(
                "h-10 w-full rounded-md border py-2 pl-10 pr-3 text-sm outline-none focus:border-[#0b84d8] focus-visible:ring-2 focus-visible:ring-[#0b84d8]/60",
                wa.border,
                wa.input
              )}
            />
          </div>
          <button
            type="button"
            onClick={() => {
              loadFolders();
              loadMessages();
            }}
            className={classNames("rounded-full p-2 transition", wa.muted, wa.hover, "hover:text-wa-text")}
            aria-label="Vernieuwen"
          >
            <ArrowPathIcon className={classNames("h-5 w-5", loadingList ? "animate-spin" : "")} />
          </button>
        </header>

        <div className={classNames("flex h-14 flex-shrink-0 items-center justify-between border-b px-4", wa.border)}>
          <h1 className="text-xl font-semibold">
            {folderNavigation.find((item) => item.path === activeFolder)?.name || "Postvak in"}
          </h1>
          <div className={classNames("flex items-center gap-2 text-sm", wa.muted)}>
            <span>{filteredMessages.length ? `${filteredMessages.length} e-mails` : "0 e-mails"}</span>
          </div>
        </div>

        {setup ? (
          <div className="flex flex-1 items-center justify-center p-6 text-center">
            <div className={classNames("max-w-lg rounded-md border p-6", wa.border, wa.panel2)}>
              <EnvelopeIcon className="mx-auto h-12 w-12 text-wa-muted" />
              <h2 className="mt-4 text-lg font-semibold">Mail configuratie ontbreekt</h2>
              <p className={classNames("mt-2 text-sm", wa.muted)}>
                De mailbox login ontbreekt in de server environment voor{" "}
                {mailConfig?.from || "info@repeathealth.com"}. Controleer `ADMIN_MAIL_SMTP_USER`
                en `ADMIN_MAIL_SMTP_PASSWORD`.
              </p>
              <p className={classNames("mt-3 text-xs", wa.muted)}>
                Ontbrekend: {(setup.missing || []).join(", ")}
              </p>
            </div>
          </div>
        ) : error ? (
          <div className="m-4 rounded-md border border-red-300 bg-red-50 p-4 text-sm text-red-800 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-100">
            <p className="font-semibold">E-mailbox kon niet geladen worden</p>
            <p className="mt-1">{error}</p>
            {mailConfig?.imap ? (
              <p className="mt-2 text-xs opacity-80">
                Controleer IMAP: {mailConfig.imap.host}:{mailConfig.imap.port}
              </p>
            ) : null}
          </div>
        ) : (
          <div className="min-h-0 flex-1 overflow-y-auto">
            {filteredMessages.map((message) => {
              const active = message.id === selectedId;
              const checked = selectedIds.includes(message.id);
              return (
                <div
                  key={message.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleOpenMessage(message.id)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      handleOpenMessage(message.id);
                    }
                  }}
                  className={classNames(
                    "grid w-full cursor-pointer grid-cols-[auto_1fr_auto] gap-3 border-b px-4 py-3 text-left transition",
                    wa.border,
                    active ? "bg-[#e8f3ff] dark:bg-[#141f26]" : "hover:bg-wa-hover"
                  )}
                >
                  <div className="flex items-start gap-3 pt-1">
                    <button
                      type="button"
                      onClick={(event) => handleSelectMessageCheckbox(message, event)}
                      className={classNames(
                        "flex h-6 w-6 items-center justify-center rounded border transition",
                        checked
                          ? "border-[#0b84d8] bg-[#0b84d8] text-white"
                          : "border-wa-muted text-transparent hover:border-[#0b84d8]"
                      )}
                      aria-label={checked ? "E-mail deselecteren" : "E-mail selecteren"}
                      aria-pressed={checked}
                    >
                      <CheckIcon className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={(event) => handleToggleFavorite(message, event)}
                      disabled={togglingFlagIds.includes(message.id)}
                      className={classNames(
                        "flex h-6 w-6 items-center justify-center rounded transition hover:bg-wa-hover",
                        message.flagged ? "text-[#f5c542]" : "text-wa-muted"
                      )}
                      aria-label={message.flagged ? "Favoriet verwijderen" : "Als favoriet markeren"}
                      aria-pressed={message.flagged}
                    >
                      <StarIcon className={classNames("h-5 w-5", message.flagged ? "fill-current" : "")} />
                    </button>
                    {!message.seen ? <span className="mt-1.5 h-2.5 w-2.5 rounded-full bg-[#0b84d8]" /> : null}
                  </div>
                  <div className="min-w-0">
                    <div className="flex min-w-0 items-center gap-2">
                      <p className={classNames("truncate text-sm", message.seen ? "font-medium" : "font-semibold")}>
                        {message.sender}
                      </p>
                      {message.hasAttachments ? <PaperClipIcon className="h-4 w-4 flex-shrink-0 text-wa-muted" /> : null}
                    </div>
                    <p className="mt-1 truncate text-sm font-medium">{message.subject}</p>
                    <p className={classNames("mt-0.5 truncate text-sm", wa.muted)}>
                      {message.snippet || message.senderEmail || "Geen voorbeeldtekst beschikbaar"}
                    </p>
                  </div>
                  <div className={classNames("flex flex-col items-end gap-2 text-xs", wa.muted)}>
                    <span>{formatDate(message.date)}</span>
                    <EllipsisVerticalIcon className="h-5 w-5" />
                  </div>
                </div>
              );
            })}
            {!loadingList && filteredMessages.length === 0 ? (
              <div className={classNames("px-6 py-16 text-center text-sm", wa.muted)}>
                Geen e-mails gevonden.
              </div>
            ) : null}
          </div>
        )}
      </section>

      {messageDetailOpen ? (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/35 p-4">
          <section className={classNames("flex h-[min(780px,90dvh)] w-full max-w-5xl flex-col overflow-hidden rounded-lg border shadow-2xl", wa.border, wa.panel)}>
            <header className={classNames("flex h-14 flex-shrink-0 items-center justify-between border-b px-5", wa.border, wa.panel2)}>
              <div className="flex min-w-0 items-center gap-3">
                <button
                  type="button"
                  onClick={() => setMessageDetailOpen(false)}
                  className={classNames("rounded-md border p-1.5", wa.border, wa.hover)}
                  aria-label="Sluit e-mail"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
                <h2 className="truncate text-lg font-semibold">
                  {loadingMessage ? "E-mail laden..." : selectedMessage?.subject || "E-mail"}
                </h2>
              </div>
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => openCompose("reply")} disabled={!selectedMessage} className={classNames("inline-flex h-9 items-center gap-2 rounded-md px-3 text-sm font-medium", selectedMessage ? "hover:bg-wa-hover" : "cursor-not-allowed opacity-45")}>
                  <ArrowUturnLeftIcon className="h-4 w-4" />
                  Beantwoorden
                </button>
                <button type="button" onClick={() => openCompose("replyAll")} disabled={!selectedMessage} className={classNames("hidden h-9 items-center gap-2 rounded-md px-3 text-sm font-medium sm:inline-flex", selectedMessage ? "hover:bg-wa-hover" : "cursor-not-allowed opacity-45")}>
                  Allen beantwoorden
                </button>
                <button type="button" onClick={() => openCompose("forward")} disabled={!selectedMessage} className={classNames("hidden h-9 items-center gap-2 rounded-md px-3 text-sm font-medium sm:inline-flex", selectedMessage ? "hover:bg-wa-hover" : "cursor-not-allowed opacity-45")}>
                  Doorsturen
                </button>
                <button type="button" onClick={() => handleMarkSeen(!selectedMessage?.seen)} disabled={!selectedMessage} className={classNames("hidden h-9 items-center gap-2 rounded-md px-3 text-sm font-medium lg:inline-flex", selectedMessage ? "hover:bg-wa-hover" : "cursor-not-allowed opacity-45")}>
                  {selectedMessage?.seen ? "Markeer ongelezen" : "Markeer gelezen"}
                </button>
                <label className={classNames("hidden h-9 items-center gap-2 rounded-md px-3 text-sm font-medium md:inline-flex", selectedMessage ? "hover:bg-wa-hover" : "opacity-45")}>
                  <FolderArrowDownIcon className="h-4 w-4" />
                  Verplaatsen
                  <select disabled={!selectedMessage} defaultValue="" onChange={(event) => handleMoveMessage(event.target.value)} className="max-w-32 bg-transparent text-sm outline-none">
                    <option value="" disabled>Map</option>
                    {targetFolders.map((folder) => (
                      <option key={folder.path} value={folder.path}>{folder.name}</option>
                    ))}
                  </select>
                </label>
                <button type="button" onClick={handleDeleteMessage} disabled={!canDeleteActiveMessage} className={classNames("inline-flex h-9 items-center gap-2 rounded-md px-3 text-sm font-medium", canDeleteActiveMessage ? "hover:bg-wa-hover" : "cursor-not-allowed opacity-45")} title={activeFolder === "INBOX" ? "Mails in Postvak in worden niet verwijderd" : "Verwijderen"}>
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </header>
            {loadingMessage ? (
              <div className={classNames("flex flex-1 items-center justify-center text-sm", wa.muted)}>
                E-mail laden...
              </div>
            ) : selectedMessage ? (
              <div className="min-h-0 flex-1 overflow-y-auto px-7 py-6">
                <div className="mb-6 flex items-start gap-4">
                  <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-[#c7b2ea] text-sm font-bold text-[#472c76]">
                    {(selectedMessage.sender || "?").slice(0, 2).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold">{selectedMessage.sender}</p>
                    {selectedMessage.to?.length ? (
                      <p className={classNames("mt-1 truncate text-xs", wa.muted)}>
                        Aan: {selectedMessage.to.join(", ")}
                      </p>
                    ) : null}
                  </div>
                  <p className={classNames("ml-auto flex-shrink-0 text-xs", wa.muted)}>{formatDate(selectedMessage.date)}</p>
                </div>
                {selectedHtml ? (
                  <article
                    className="prose max-w-none text-wa-text prose-a:text-[#0b84d8] dark:prose-invert"
                    dangerouslySetInnerHTML={{ __html: selectedHtml }}
                  />
                ) : (
                  <pre className="whitespace-pre-wrap font-sans text-sm leading-6 text-wa-text">
                    {selectedMessage.text || "Geen inhoud beschikbaar."}
                  </pre>
                )}
                {selectedMessage.attachments?.length ? (
                  <div className="mt-8 grid gap-2 sm:grid-cols-2">
                    {selectedMessage.attachments.map((attachment) => (
                      <button
                        key={attachment.id}
                        type="button"
                        onClick={() => handleDownloadAttachment(attachment)}
                        className={classNames("flex items-center gap-3 rounded-md border p-3 text-left transition hover:bg-wa-hover", wa.border, wa.panel2)}
                      >
                        <PaperClipIcon className="h-5 w-5 flex-shrink-0 text-wa-muted" />
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium">{attachment.filename}</p>
                          <p className={classNames("text-xs", wa.muted)}>{attachment.contentType} · Downloaden</p>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : (
              <div className={classNames("flex flex-1 items-center justify-center text-sm", wa.muted)}>
                E-mail kon niet geopend worden.
              </div>
            )}
          </section>
        </div>
      ) : null}

      {composeOpen && composeMinimized ? (
        <div className={classNames("fixed bottom-0 right-4 z-50 flex w-[min(30rem,calc(100vw-2rem))] items-center overflow-hidden rounded-t-md border shadow-2xl", wa.border, wa.panel)}>
          <button
            type="button"
            onClick={() => setComposeMinimized(false)}
            className="flex min-w-0 flex-1 items-center gap-2 px-4 py-3 text-left hover:bg-wa-hover"
          >
            <PencilSquareIcon className="h-5 w-5 flex-shrink-0 text-[#0b84d8]" />
            <span className="min-w-0 flex-1 truncate text-sm font-semibold">
              {compose.subject || "Nieuw bericht"}
            </span>
          </button>
          <button
            type="button"
            onClick={() => {
              setComposeOpen(false);
              setComposeMinimized(false);
            }}
            className="p-3 text-wa-muted hover:bg-wa-hover hover:text-wa-text"
            aria-label="Concept sluiten"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      ) : null}

      {composeOpen && !composeMinimized ? (
        <div className="fixed bottom-0 right-0 z-50 flex w-full justify-end p-3 pointer-events-none sm:p-6">
          <section className={classNames("pointer-events-auto flex h-[min(760px,86dvh)] w-full max-w-3xl flex-col overflow-hidden rounded-md border shadow-2xl", wa.border, wa.panel)}>
            <header className="flex h-14 flex-shrink-0 items-center justify-between bg-[#4d63aa] px-5 text-white">
              <h2 className="text-lg font-semibold">Nieuw bericht</h2>
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => setComposeMinimized(true)} className="rounded-full p-1.5 hover:bg-white/10" aria-label="Concept minimaliseren">
                  <MinusIcon className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setComposeOpen(false);
                    setComposeMinimized(false);
                  }}
                  className="rounded-full p-1 hover:bg-white/10"
                  aria-label="Concept sluiten"
                >
                  <XMarkIcon className="h-7 w-7" />
                </button>
              </div>
            </header>
            <div className="flex-shrink-0 border-b border-wa-border">
              {[
                ["to", "Aan:"],
                ["cc", "Cc:"],
                ["bcc", "Bcc:"],
              ].map(([key, label]) => (
                <RecipientPicker
                  key={key}
                  field={key}
                  label={label}
                  value={compose[key]}
                  recipients={recipientOptions}
                  loading={loadingRecipients}
                  onChange={(nextRecipients) =>
                    setCompose((current) => ({ ...current, [key]: nextRecipients }))
                  }
                />
              ))}
              <label className="flex min-h-12 items-center gap-3 px-5">
                <span className={classNames("w-20 flex-shrink-0 text-sm", wa.muted)}>Onderwerp:</span>
                <input
                  value={compose.subject}
                  onChange={(event) => setCompose((current) => ({ ...current, subject: event.target.value }))}
                  className="h-10 min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-wa-muted focus-visible:ring-2 focus-visible:ring-[#0b84d8]/60"
                />
              </label>
            </div>
            <div className="min-h-0 flex-1 bg-wa-panel px-5 py-4">
              <ReactQuill
                className="admin-mailbox-richtext h-full"
                value={compose.body}
                onChange={(content) => setCompose((current) => ({ ...current, body: content }))}
                modules={richTextModules}
                formats={richTextFormats}
                placeholder="Schrijf je e-mail..."
              />
            </div>
            {attachments.length ? (
              <div className={classNames("grid flex-shrink-0 gap-2 border-t px-5 py-3 sm:grid-cols-2", wa.border)}>
                {attachments.map((attachment) => (
                  <div key={attachment.id} className={classNames("flex min-w-0 items-center gap-2 rounded-md border px-3 py-2", wa.border, wa.panel2)}>
                    <PaperClipIcon className="h-5 w-5 flex-shrink-0 text-wa-muted" />
                    <span className="min-w-0 flex-1 truncate text-sm">{attachment.filename}</span>
                    <button
                      type="button"
                      onClick={() => setAttachments((current) => current.filter((item) => item.id !== attachment.id))}
                      className={classNames("rounded-full p-1", wa.muted, wa.hover)}
                      aria-label={`${attachment.filename} verwijderen`}
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            ) : null}
            <footer className={classNames("flex flex-shrink-0 items-center justify-between gap-3 border-t px-5 py-4", wa.border, wa.panel2)}>
              <div className={classNames("text-sm", sendStatus ? "text-wa-text" : wa.muted)}>
                {sendStatus ||
                  `SMTP: ${mailConfig?.smtp?.host || "send.one.com"}:${mailConfig?.smtp?.port || 587}`}
              </div>
              <div className="flex items-center gap-2">
                <label className={classNames("inline-flex h-10 cursor-pointer items-center gap-2 rounded-md border border-wa-border px-3 text-sm font-semibold hover:bg-wa-hover", wa.muted)}>
                  <PaperClipIcon className="h-5 w-5" />
                  Bijvoegen
                  <input type="file" multiple className="hidden" onChange={handleAttachmentChange} />
                </label>
                <button
                  type="button"
                  onClick={handleSaveDraft}
                  disabled={savingDraft}
                  className="inline-flex h-10 items-center gap-2 rounded-md border border-wa-border px-4 text-sm font-semibold text-wa-text hover:bg-wa-hover disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {savingDraft ? "Opslaan..." : "Concept opslaan"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setComposeOpen(false);
                    setComposeMinimized(false);
                  }}
                  className="inline-flex h-10 items-center gap-2 rounded-md border border-[#0b84d8] px-4 text-sm font-semibold text-wa-text hover:bg-wa-hover"
                >
                  <TrashIcon className="h-5 w-5" />
                  Verwijderen
                </button>
                <button
                  type="button"
                  onClick={handleSend}
                  disabled={sending || compose.to.length === 0}
                  className="inline-flex h-10 items-center gap-2 rounded-md bg-[#0b84d8] px-4 text-sm font-semibold text-white transition hover:bg-[#1494ee] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <PaperAirplaneIcon className="h-5 w-5" />
                  {sending ? "Verzenden..." : "Verzenden"}
                </button>
              </div>
            </footer>
          </section>
        </div>
      ) : null}
    </div>
  );
};

export default AdminMailbox;
